/**
 * AbsorptionGameWidget - воронка по уровням и винрейты прототипа alienabsorption
 * ===============================================================================
 *
 * Период из сайдбара — ДАТА УСТАНОВКИ когорты; забеги когорты — за всё время.
 * График 1: % когорты, начавшей уровень. График 2: винрейт уровня (по попыткам).
 * Таблица: строка на уровень.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { ApiService } from '../../services/api';
import { FiltersService } from '../../services/filtersService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { prototypes as prototypesSpecs } from '@ggstat/chart-specs';

const fmtInt = (value) => Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (value) => (value === null || value === undefined ? '—' : `${value}%`);
const fmtSec = (value) => (value === null || value === undefined ? '—' : `${fmtInt(value)} сек`);

const FunnelTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">Уровень {label}</p>
      <p>Начали: {fmtInt(row.started)} ({fmtPct(row.startedPctOfCohort)} когорты)</p>
      <p className="text-muted-foreground">от предыдущего уровня: {fmtPct(row.startedPctOfPrevious)}</p>
    </div>
  );
};

const WinrateTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">Уровень {label}</p>
      <p>Винрейт: {fmtPct(row.winrate)}</p>
      <p className="text-muted-foreground">
        победы {fmtInt(row.wins)} · поражения {fmtInt(row.losses)} · попытки {fmtInt(row.attempts)}
      </p>
    </div>
  );
};

const SPEC = prototypesSpecs.absorptionGame;
const FUNNEL_SERIES = SPEC.funnelSeries[0];
const WINRATE_SERIES = SPEC.winrateSeries[0];
const FORMATTERS = { int: fmtInt, percent: fmtPct, seconds: fmtSec };
const TABLE_COLUMNS = SPEC.tableColumns.map((col) => ({ ...col, fmt: FORMATTERS[col.format] }));

// Контент уровней у поколений разный, поэтому они показываются порознь, а не суммой
const GENERATIONS = [
  { id: 'current', label: 'Клиент 1.1+' },
  { id: 'legacy', label: 'Клиент 0.1.6 (старая схема)' }
];

export function AbsorptionGameWidget() {
  const [generation, setGeneration] = useState('current');
  const widgetData = useWidgetData(async (filters, options) => {
    const params = {
      from: filters.startDate ? FiltersService.formatDate(filters.startDate) : undefined,
      to: filters.endDate ? FiltersService.formatDate(filters.endDate) : undefined,
      generation
    };
    return ApiService.get('/prototypes/absorption-game', params, { signal: options?.signal });
  }, [generation]);

  const widgetProps = useBaseWidget(widgetData);
  const levels = useMemo(
    () => (Array.isArray(widgetProps.data?.levels) ? widgetProps.data.levels : []),
    [widgetProps.data]
  );
  const cohortSize = widgetProps.data?.cohortSize;
  const cohortLabel = cohortSize === undefined || cohortSize === null ? '—' : fmtInt(cohortSize);

  return (
    <BaseWidget
      {...widgetProps}
      title="Absorption Game: воронка по уровням и винрейты"
      subtitle={`Когорта по первому first_open (период — даты установки), забеги за всё время. Когорта: ${cohortLabel}`}
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={8}
      toolbar={
        <div className="flex flex-wrap gap-2">
        {GENERATIONS.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={generation === item.id}
            onClick={() => setGeneration(item.id)}
            className={`px-3 py-1 rounded-md border text-sm ${
              generation === item.id ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
            }`}
          >
            {item.label}
          </button>
        ))}
        </div>
      }
    >
      {levels.length === 0 ? (
        <p className="text-sm text-muted-foreground">У когорты установки в выбранном периоде нет забегов</p>
      ) : (
        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Начали уровень, % когорты</h4>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={levels} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="level" tick={{ fill: 'currentColor' }} className="text-xs" minTickGap={12} />
                <YAxis domain={[0, 100]} tick={{ fill: 'currentColor' }} className="text-xs" unit="%" />
                <Tooltip content={<FunnelTooltip />} />
                <Line
                  type="monotone"
                  dataKey={FUNNEL_SERIES.key}
                  name={FUNNEL_SERIES.label}
                  stroke={FUNNEL_SERIES.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">Винрейт уровня, %</h4>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={levels} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="level" tick={{ fill: 'currentColor' }} className="text-xs" minTickGap={12} />
                <YAxis domain={[0, 100]} tick={{ fill: 'currentColor' }} className="text-xs" unit="%" />
                <Tooltip content={<WinrateTooltip />} />
                <Line
                  type="monotone"
                  dataKey={WINRATE_SERIES.key}
                  name={WINRATE_SERIES.label}
                  stroke={WINRATE_SERIES.color}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground">
              Винрейт = победы / (победы + поражения), по попыткам. Незавершённые попытки в винрейт не входят.
            </p>
          </div>

          <div className="overflow-auto max-h-[600px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="sticky top-0 bg-background py-2 pr-4 font-medium">Уровень</th>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} className="sticky top-0 bg-background py-2 pr-4 font-medium text-right" title={col.title}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {levels.map((row) => (
                  <tr key={row.level} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium">{row.level}</td>
                    {TABLE_COLUMNS.map((col) => (
                      <td key={col.key} className="py-2 pr-4 text-right">{col.fmt(row[col.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Начали / прошли — игроки (уникальные устройства), начавшие / выигравшие уровень. Попытки, победы, поражения — забеги. Поколения клиента не суммируются: контент уровней разный. Когорта — новые установки, без апгрейдов с 0.1.6.
            Уровень у run_end без номера (доигран после перезапуска) берётся из acc_progress; их длительность в медиану не входит.
          </p>
        </div>
      )}
    </BaseWidget>
  );
}

export default AbsorptionGameWidget;
